import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './generating-project-starting-logic-overlay.css';
import { getAccessToken, getRefreshToken } from '../../../auth';
import Button from '../../../lib/button/Button';
import Loader from '../../../lib/loader/Loader';
import { IComponentLifecycleProps } from '../../../utils/react';
import { EditorService } from '../../../services/editor';
import { DTOAction, LiveMessageEventType } from '@elyx-code/definitions';
import { ProjectState } from '@elyx-code/project-logic-tree';

export interface IGeneratingProjectStartingLogicOverlayProps extends IComponentLifecycleProps {
	project: EditorService;
	ready: boolean;
}

interface IRateLimitState {
	attempt: number;
	waitMs: number;
	willRetry: boolean;
	since: number;
}

const GeneratingProjectStartingLogicOverlay: React.FC<
	IGeneratingProjectStartingLogicOverlayProps
> = ({ project, ready }) => {
	const [aborting, setAborting] = useState<boolean>(false);
	const [rateLimit, setRateLimit] = useState<IRateLimitState | null>(null);
	const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
	const { id: projectId, taskId } = useParams<{
		id: string;
		taskId: string;
	}>();
	const server = project?.socket;

	const stopProjectLogicGeneration = useCallback(async () => {
		setAborting(true);

		const [accessToken, refreshToken] = await Promise.all([
			getAccessToken(),
			getRefreshToken(),
		]);

		server?.send({
			action: 'abort-project-starting-logic-generation' as any as DTOAction,
			actionId: taskId,
			userAuthor: ProjectState.sessionAuthor,
			agentAuthor: null,
			emittedAt: new Date().toISOString(),
			channel: project.PROJECT_NOTIFICATIONS_CHANNEL,
			sessionId: project.sessionId,
			authorization: 'Bearer ' + accessToken,
			refreshToken: refreshToken,
			workspaceId: project.activeWorkspace?.id || null,
			type: LiveMessageEventType.Post,
			event: 'abort-project-starting-logic-generation',
			message: null,
			payload: {
				projectId: project.projectId,
			},
		});
	}, [server, projectId, taskId]);

	useEffect(() => {
		if (!!project && ready) {
			project.disableCanvasGlobally();
		}
	}, [project, ready]);

	useEffect(() => {
		if (!project) return;

		const onRateLimited = (payload: {
			attempt: number;
			waitMs: number;
			willRetry: boolean;
		}) => {
			setRateLimit({
				attempt: payload.attempt,
				waitMs: payload.waitMs,
				willRetry: payload.willRetry,
				since: Date.now(),
			});
		};
		const onResolved = () => setRateLimit(null);

		project.on('generation-rate-limited', onRateLimited);
		project.on('generation-rate-limit-resolved', onResolved);

		return () => {
			project.off('generation-rate-limited', onRateLimited);
			project.off('generation-rate-limit-resolved', onResolved);
		};
	}, [project]);

	useEffect(() => {
		if (!rateLimit) {
			setSecondsRemaining(0);
			return;
		}

		const tick = () => {
			const elapsed = Date.now() - rateLimit.since;
			const remaining = Math.max(0, rateLimit.waitMs - elapsed);
			setSecondsRemaining(Math.ceil(remaining / 1000));
		};

		tick();
		const interval = window.setInterval(tick, 500);
		return () => window.clearInterval(interval);
	}, [rateLimit]);

	if (!ready) {
		return null;
	}

	return (
		<div
			style={{
				position: 'fixed',
				top: '50%',
				left: '50%',
				transform: 'translate(-50%, -50%)',
				backgroundColor: 'rgba(0, 0, 0, 0.2)', // Same black partial opacity
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				textAlign: 'center',
				padding: '32px', // Added padding to give it a box shape
				borderRadius: '12px', // Rounded edges for a better modal look
				backdropFilter: 'blur(4px)', // Optional: adds a slight blur behind the modal for readability
				zIndex: 50, // Ensures it stays above other canvas elements
				pointerEvents: 'auto', // Ensures the modal itself catches clicks
			}}
		>
			<Loader size={120} color='primary' variant='text' thickness='thin' />
			<p
				style={{
					color: 'rgba(0, 0, 0, 0.7)',
					marginTop: '16px',
					marginBottom: rateLimit ? '8px' : '24px',
					fontWeight: 600,
				}}
			>
				Auto-generating project logic based on your description.
			</p>
			{rateLimit && (
				<p
					style={{
						color: 'rgba(180, 120, 0, 0.9)',
						marginTop: 0,
						marginBottom: '24px',
						fontSize: 13,
						fontWeight: 500,
					}}
				>
					{rateLimit.willRetry
						? `AI provider rate-limited — retrying in ${secondsRemaining}s (attempt ${rateLimit.attempt}).`
						: `AI provider rate-limited — no more retries will be attempted.`}
				</p>
			)}
			<Button
				color='error'
				size='small'
				className={`abort-generation-button ${aborting ? 'disabled' : ''}`}
				disabled={aborting}
				onClick={stopProjectLogicGeneration}
			>
				Abort
			</Button>
		</div>
	);
};

export default GeneratingProjectStartingLogicOverlay;
